import React, { useState, useEffect, useCallback } from 'react';
import NavBar from '../NavBar.js';
import styles from '../styles/MediosPago.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

// Firebase (centralizado)
import { auth, db } from '../../firebaseConfig';
import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp, 
  collection, 
  getDocs, 
  query, 
  where, 
  updateDoc,
  addDoc 
} from 'firebase/firestore';

// Imágenes locales
import redebanLogo from '../../assets/redBank.png';
import breb from '../../assets/bre-b.png';
import redebanQR from '../../assets/redebanQR.png';
import aviso from '../../assets/aviso.jpg';
import binanceLogo from '../../assets/binance.png'; 
import binanceQR from '../../assets/binanceQR.png';

const CREDENTIALS_COVER_IMG = aviso;
const CLOUD_FN_BASE_URL = process.env.REACT_APP_VERIFY_FN_URL;




if (!CLOUD_FN_BASE_URL) {
  console.error('REACT_APP_VERIFY_FN_URL no está configurada');
  throw new Error('REACT_APP_VERIFY_FN_URL no está configurada en el archivo .env');
}




const TERMS_CONDITIONS_URL = "https://drive.google.com/file/d/1aESo-IebajZ8BaTIrkh4LPeCuOd7NuU8/view?usp=drive_link";



// ===============================
// Guardado ÚNICO en Firestore
// ===============================
async function guardarPaycheckUnico(pagoData) {
  const user = auth.currentUser;

  const idmensaje =
    pagoData?.mensajeId ??
    `${pagoData?.fecha || 'sinf'}_${pagoData?.hora || 'sinh'}_${(pagoData?.nombreCompleto || pagoData?.usuario || 'sinN').replace(/\s+/g, '_')}_${pagoData?.monto || 'sinM'}`;

  const payload = {
    uid: user?.uid ?? null,
    fecha: pagoData?.fecha ?? null,
    hora: pagoData?.hora ?? null,
    flag: false,
    monto: pagoData?.monto ?? null,
    nombre: pagoData?.nombreCompleto ?? pagoData?.usuario ?? null,
    idmensaje: idmensaje,
    createdAt: serverTimestamp(),
  };

  const ref = doc(db, 'Paycheck', idmensaje);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return { already: true, id: ref.id };
  }

  await setDoc(ref, payload);
  return { already: false, id: ref.id };
}

// ===============================
// Función para obtener productos del carrito desde el catálogo
// ===============================
async function obtenerProductosCarrito(totalPago) {
  try {
    // Obtener todos los productos de ambos catálogos
    const catalogoSnapshot = await getDocs(collection(db, 'catalogo'));
    const catalogoVendedoresSnapshot = await getDocs(collection(db, 'catalogoVendedores'));
    
    const todosProductos = [
      ...catalogoSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      ...catalogoVendedoresSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    ];

    // Esta función intentará identificar qué productos podrían componer el total
    // En un escenario real, sería mejor pasar la información del carrito desde PageStore
    // Por ahora, buscaremos combinaciones que sumen el total
    const combinacionesValidas = [];
    
    // Buscar productos individuales que coincidan exactamente
    todosProductos.forEach(producto => {
      const precioCOP = parseFloat(producto.precioConDescuentoCOP || producto.precioBaseCOP || '0');
      const precioUSD = parseFloat(producto.precioConDescuentoUSD || producto.precioBaseUSD || '0');
      
      if (Math.abs(precioCOP - totalPago) < 1 || Math.abs(precioUSD - totalPago) < 0.01) {
        combinacionesValidas.push([{
          ...producto,
          cantidad: 1,
          precioUnitario: Math.abs(precioCOP - totalPago) < 1 ? precioCOP : precioUSD
        }]);
      }
    });

    // Si no encontramos una coincidencia exacta, intentar combinaciones simples
    if (combinacionesValidas.length === 0) {
      // Buscar productos que multiplicados por cantidad puedan dar el total
      for (let producto of todosProductos) {
        const precioCOP = parseFloat(producto.precioConDescuentoCOP || producto.precioBaseCOP || '0');
        const precioUSD = parseFloat(producto.precioConDescuentoUSD || producto.precioBaseUSD || '0');
        
        for (let cantidad = 2; cantidad <= 10; cantidad++) {
          if (Math.abs((precioCOP * cantidad) - totalPago) < 1) {
            combinacionesValidas.push([{
              ...producto,
              cantidad: cantidad,
              precioUnitario: precioCOP
            }]);
            break;
          }
          if (Math.abs((precioUSD * cantidad) - totalPago) < 0.01) {
            combinacionesValidas.push([{
              ...producto,
              cantidad: cantidad,
              precioUnitario: precioUSD
            }]);
            break;
          }
        }
      }
    }

    return combinacionesValidas.length > 0 ? combinacionesValidas[0] : null;
  } catch (error) {
    console.error('Error obteniendo productos del carrito:', error);
    return null;
  }
}

// ===============================
// Función para buscar cuentas disponibles
// ===============================
async function buscarCuentasDisponibles(plataforma, pantallasRequeridas, unidad) {
  try {
    const cuentasQuery = query(
      collection(db, 'Cuentas'),
      where('plataforma', '==', plataforma)
    );
    const cuentasSnapshot = await getDocs(cuentasQuery);
    
    const cuentasDisponibles = [];
    
    for (const cuentaDoc of cuentasSnapshot.docs) {
      const cuenta = cuentaDoc.data();
      
      // Buscar por campo 'codigo' en lugar de usar el ID del documento
      const profilesQuery = query(
        collection(db, 'profiles'),
        where('codigo', '==', cuenta.codigo)
      );
      const profilesSnapshot = await getDocs(profilesQuery);
      
      if (profilesSnapshot.empty) continue;
      
      // Tomar el primer documento que coincida con el código
      const profilesDoc = profilesSnapshot.docs[0];
      const profilesData = profilesDoc.data();
      const perfiles = profilesData.perfiles || {};
      
      if (unidad === 'completa') {
        // Para cuenta completa, verificar que la cuenta tenga las pantallas exactas y ningún perfil esté vendido
        const pantallasDisponibles = Object.values(perfiles).filter(perfil => !perfil.vendido).length;
        
        if (pantallasDisponibles === parseInt(cuenta.pantallas) && pantallasDisponibles >= pantallasRequeridas) {
          cuentasDisponibles.push({
            codigo: cuenta.codigo,
            correo: cuenta.correo,
            contrasena: cuenta.contrasena,
            plataforma: cuenta.plataforma,
            pantallas: cuenta.pantallas,
            perfiles: perfiles,
            profilesDocId: profilesDoc.id, 
            tipo: 'completa'
          });
        }
      } else if (unidad === 'pantalla') {
        // Para pantallas individuales, verificar que haya suficientes perfiles no vendidos
        const perfilesDisponibles = Object.entries(perfiles).filter(([key, perfil]) => !perfil.vendido);
        
        if (perfilesDisponibles.length >= pantallasRequeridas) {
          cuentasDisponibles.push({
            codigo: cuenta.codigo,
            correo: cuenta.correo,
            contrasena: cuenta.contrasena,
            plataforma: cuenta.plataforma,
            pantallas: cuenta.pantallas,
            perfiles: perfiles,
            perfilesDisponibles: perfilesDisponibles,
            profilesDocId: profilesDoc.id, // NUEVO: Guardar el ID del documento para updates
            tipo: 'pantalla'
          });
        }
      }
    }
    
    return cuentasDisponibles;
    
  } catch (error) {
    console.error('Error buscando cuentas disponibles:', error);
    return [];
  }
}

// ===============================
// Función para marcar perfiles como vendidos y actualizar pantallas en Cuentas
// ===============================
async function marcarPerfilesVendidos(codigo, perfilesKeys, tipo, pantallasVendidas) {
  try {
    // Buscar el documento por el campo 'codigo' en lugar del ID
    const profilesQuery = query(
      collection(db, 'profiles'),
      where('codigo', '==', codigo)
    );
    const profilesSnapshot = await getDocs(profilesQuery);
    
    if (profilesSnapshot.empty) {
      throw new Error(`No se encontró información de perfiles para el código: ${codigo}`);
    }
    
    // Tomar el primer documento que coincida
    const profilesDoc = profilesSnapshot.docs[0];
    const profilesData = profilesDoc.data();
    const perfiles = { ...profilesData.perfiles };
    
    if (tipo === 'completa') {
      // Marcar todos los perfiles como vendidos
      Object.keys(perfiles).forEach(key => {
        perfiles[key].vendido = true;
      });
    } else if (tipo === 'pantalla') {
      // Marcar solo los perfiles específicos como vendidos
      perfilesKeys.forEach(key => {
        if (perfiles[key]) {
          perfiles[key].vendido = true;
        }
      });
    }

    await updateDoc(profilesDoc.ref, { perfiles });

    // Actualizar pantallas en la colección Cuentas
    const cuentasQuery = query(
      collection(db, 'Cuentas'),
      where('codigo', '==', codigo)
    );
    const cuentasSnapshot = await getDocs(cuentasQuery);
    
    if (!cuentasSnapshot.empty) {
      const cuentaDoc = cuentasSnapshot.docs[0];
      const cuentaData = cuentaDoc.data();
      const pantallasActuales = parseInt(cuentaData.pantallas || '0');
      const nuevasPantallas = Math.max(0, pantallasActuales - pantallasVendidas);
      
      await updateDoc(cuentaDoc.ref, { pantallas: nuevasPantallas });
      console.log(`Pantallas actualizadas en cuenta ${codigo}: ${pantallasActuales} -> ${nuevasPantallas}`);
    }
    
    return true;
  } catch (error) {
    console.error('Error marcando perfiles como vendidos:', error);
    return false;
  }
}

// ===============================
// Función para procesar múltiples productos del carrito
// ===============================
async function procesarCarrito(productosCarrito) {
  const credencialesEntregadas = [];
  const erroresInternes = []; // Solo para logging interno

  for (const itemCarrito of productosCarrito) {
    const plataforma = itemCarrito.plataforma;
    const pantallasRequeridas = parseInt(itemCarrito.pantallas || '1');
    const unidad = itemCarrito.unidad;
    const cantidad = itemCarrito.cantidad || 1;

    // Procesar cada cantidad del producto
    for (let i = 0; i < cantidad; i++) {
      try {
        // Buscar cuentas disponibles para este producto específico
        const cuentasDisponibles = await buscarCuentasDisponibles(plataforma, pantallasRequeridas, unidad);
        
        if (cuentasDisponibles.length === 0) {
          console.error(`Sin stock para ${plataforma} (${pantallasRequeridas} pantalla(s), ${unidad})`);
          erroresInternes.push(`No hay stock disponible para ${plataforma} (${pantallasRequeridas} pantalla(s), ${unidad})`);
          continue;
        }

        // Seleccionar la primera cuenta disponible
        const cuentaSeleccionada = cuentasDisponibles[0];
        let credencialEntregada = {};

        if (unidad === 'completa') {
          // Entregar cuenta completa
          credencialEntregada = {
            tipo: 'Cuenta Completa',
            codigo: cuentaSeleccionada.codigo,
            correo: cuentaSeleccionada.correo,
            contrasena: cuentaSeleccionada.contrasena,
            plataforma: cuentaSeleccionada.plataforma,
            pantallas: cuentaSeleccionada.pantallas,
            perfiles: Object.entries(cuentaSeleccionada.perfiles).map(([key, perfil]) => ({
              nombre: perfil.displayName,
              pin: perfil.pin
            }))
          };

          // Marcar todos los perfiles como vendidos
          await marcarPerfilesVendidos(cuentaSeleccionada.codigo, [], 'completa', parseInt(cuentaSeleccionada.pantallas));

        } else if (unidad === 'pantalla') {
          // Entregar perfiles específicos - SOLO la cantidad comprada
          const perfilesParaEntregar = cuentaSeleccionada.perfilesDisponibles.slice(0, pantallasRequeridas);
          
          credencialEntregada = {
            tipo: `${pantallasRequeridas} Pantalla(s)`,
            codigo: cuentaSeleccionada.codigo,
            correo: cuentaSeleccionada.correo,
            contrasena: cuentaSeleccionada.contrasena,
            plataforma: cuentaSeleccionada.plataforma,
            perfiles: perfilesParaEntregar.map(([key, perfil]) => ({
              nombre: perfil.displayName,
              pin: perfil.pin
            }))
          };

          // Marcar solo los perfiles entregados como vendidos y restar pantallas
          const keysVendidos = perfilesParaEntregar.map(([key]) => key);
          await marcarPerfilesVendidos(cuentaSeleccionada.codigo, keysVendidos, 'pantalla', pantallasRequeridas);
        }

        credencialesEntregadas.push(credencialEntregada);

      } catch (error) {
        console.error(`Error procesando ${plataforma}:`, error);
        erroresInternes.push(`Error procesando ${plataforma}: ${error.message}`);
      }
    }
  }

  // Solo retornar errores si NO se entregó ninguna credencial
  const erroresParaUsuario = credencialesEntregadas.length === 0 ? erroresInternes : [];

  return { credencialesEntregadas, errores: erroresParaUsuario };
}

// ===============================
// Función principal para entregar credenciales de múltiples productos
// ===============================
async function entregarCredencialesCarrito(idmensaje, totalPago, nombreComprador, productosCarrito = null) {
  try {
    // 1. Verificar si ya existe el pago y fue procesado
    const paycheckRef = doc(db, 'Paycheck', idmensaje);
    const paycheckDoc = await getDoc(paycheckRef);
    
    if (paycheckDoc.exists()) {
      const paycheckData = paycheckDoc.data();
      if (paycheckData.flag === true) {
        return {
          success: false,
          message: `Esta compra ya fue entregada anteriormente con el código ${idmensaje}. Las credenciales ya fueron proporcionadas.`,
          tipo: 'ya_entregado'
        };
      }
    }

    // 2. Obtener información de los productos del carrito
    let productosAComprar = productosCarrito;
    
    if (!productosAComprar) {
      // Si no se pasan los productos, intentar identificarlos por el total
      productosAComprar = await obtenerProductosCarrito(totalPago);
      
      if (!productosAComprar) {
        return {
          success: false,
          message: 'No se pudo identificar los productos comprados. Contacta soporte.',
          tipo: 'productos_no_encontrados'
        };
      }
    }

    // 3. Procesar todos los productos del carrito
    const resultado = await procesarCarrito(productosAComprar);
    
    if (resultado.credencialesEntregadas.length === 0) {
      return {
        success: false,
        message: 'No se pudieron entregar las credenciales. ' + (resultado.errores.join('. ')),
        tipo: 'sin_stock'
      };
    }

    // 4. Marcar el pago como procesado (flag = true)
    await updateDoc(paycheckRef, { flag: true });

    return {
      success: true,
      message: 'Credenciales entregadas exitosamente',
      credenciales: resultado.credencialesEntregadas,
      errores: resultado.errores,
      codigoCompra: idmensaje
    };

  } catch (error) {
    console.error('Error entregando credenciales del carrito:', error);
    return {
      success: false,
      message: 'Error interno procesando la entrega. Contacta soporte.',
      tipo: 'error_interno'
    };
  }
}

// Función para obtener datos del usuario autenticado
async function obtenerDatosUsuario(uid) {
  try {
    const userDoc = await getDoc(doc(db, 'Usuarios', uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        email: userData.email || null,
        phoneNumber: userData.phoneNumber || null
      };
    }
    return { email: null, phoneNumber: null };
  } catch (error) {
    console.error('Error obteniendo datos del usuario:', error);
    return { email: null, phoneNumber: null };
  }
}

// Función para guardar el recibo en la colección receipts
async function guardarRecibo(pagoData, credencialesEntregadas, productosCarrito, medioPago, totalCompra) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    // Obtener datos del usuario (email y teléfono)
    const datosUsuario = await obtenerDatosUsuario(user.uid);

    // Extraer códigos de las cuentas entregadas
    const codigosCuentas = credencialesEntregadas.map(credencial => credencial.codigo);

    // Preparar listado de productos comprados
    const productosComprados = productosCarrito ? productosCarrito.map(producto => ({
      id: producto.id || null,
      plataforma: producto.plataforma || null,
      nombre: producto.nombre || producto.title || null,
      cantidad: producto.cantidad || 1,
      precioUnitario: producto.precioUnitario || null,
      pantallas: producto.pantallas || null,
      unidad: producto.unidad || null,
      duracion: producto.duracion || null,
      tipoUsuario: producto.tipoUsuario || null
    })) : [];

    // Crear el documento del recibo
    const reciboData = {
      // Información del pago
      medioPago: medioPago, // 'Redeban' o 'Bre-B'
      fecha: pagoData.fecha || null, // formato del correo
      hora: pagoData.hora || null, // formato del correo
      idmensaje: pagoData.mensajeId || pagoData.idmensaje || null, // ID único del mensaje del correo
      
      // Información del usuario
      uid: user.uid,
      correo: datosUsuario.email,
      telefono: datosUsuario.phoneNumber,
      nombreComprador: pagoData.nombreCompleto || pagoData.usuario || null,
      
      // Información financiera
      valorCompra: totalCompra, // lo que calculó el frontend
      valorTransferido: pagoData.monto || null, // lo que devolvió el correo
      
      // Productos y cuentas
      codigosCuentasEntregadas: codigosCuentas,
      productosComprados: productosComprados,
      cantidadProductos: productosComprados.length,
      
      // Metadatos
      createdAt: serverTimestamp(),
      procesado: true,
      estado: 'entregado'
    };

    // Usar el idmensaje como ID del documento para evitar duplicados
    const reciboId = pagoData.mensajeId || pagoData.idmensaje || `recibo_${Date.now()}`;
    const reciboRef = doc(db, 'receipts', reciboId);
    
    // Verificar si ya existe el recibo
    const reciboExistente = await getDoc(reciboRef);
    if (reciboExistente.exists()) {
      console.log('El recibo ya existe:', reciboId);
      return { success: true, id: reciboId, already: true };
    }

    // Guardar el recibo
    await setDoc(reciboRef, reciboData);
    console.log('Recibo guardado exitosamente:', reciboId);

    return { success: true, id: reciboId, already: false };

  } catch (error) {
    console.error('Error guardando recibo:', error);
    return { success: false, error: error.message };
  }
}

// Función para obtener el rol del usuario (tolerante)
async function obtenerRolUsuario(uid) {
  try {
    const userDoc = await getDoc(doc(db, 'Usuarios', uid));
    if (!userDoc.exists()) return null;
    const raw = userDoc.data().rol;
    return typeof raw === 'string' ? raw.trim() : null;
  } catch (error) {
    console.error('Error obteniendo rol del usuario:', error);
    return null;
  }
}


// Función para obtener duración del producto desde el catálogo correcto según el rol
async function obtenerDuracionProducto(plataforma, uid) {
  try {
    // 1) Obtener y normalizar rol
    const rolUsuario = await obtenerRolUsuario(uid);
    const rol = (rolUsuario || '').toLowerCase().trim();

    // 2) Determinar si es usuario final
    const esUsuarioFinal = ['usuarios', 'usuario', 'user', 'cliente'].includes(rol);

    // 3) Elegir colección principal y secundaria (fallback)
    const primario = esUsuarioFinal ? 'catalogo' : 'catalogoVendedores';
    const secundario = esUsuarioFinal ? 'catalogoVendedores' : 'catalogo';

    // 4) Normalizar plataforma para el where exacto
    const plataformaNorm = String(plataforma || '').trim();

    // 5) Intento 1: colección principal
    let snap = await getDocs(
      query(collection(db, primario), where('plataforma', '==', plataformaNorm))
    );

    // 6) Fallback: colección secundaria si no hubo match
    if (snap.empty) {
      console.warn(`No se encontró ${plataformaNorm} en ${primario}. Probando ${secundario}...`);
      snap = await getDocs(
        query(collection(db, secundario), where('plataforma', '==', plataformaNorm))
      );
    }

    // 7) Resultado
    if (!snap.empty) {
      const producto = snap.docs[0].data();
      return producto.duracion || null; // p.ej. "30 días"
    }

    console.warn(`No se encontró la plataforma ${plataformaNorm} en ambos catálogos.`);
    return null;
  } catch (error) {
    console.error('Error obteniendo duración del producto:', error);
    return null;
  }
}



// Función para extraer número de días de la duración (ej: "30 días" -> 30)
function extraerDiasDeDuracion(duracion) {
  if (!duracion) return 30; // valor por defecto
  
  const match = duracion.match(/(\d+)\s*d[ií]as?/i);
  return match ? parseInt(match[1], 10) : 30;
}

// Router por medio de pago → devuelve un Date en UTC
function parsearFechaPorMedio(fechaString, medioPago) {
  if (!fechaString) return null;

  // Normalizamos posibles guiones a barras por consistencia
  const s = String(fechaString).trim().replace(/-/g, '/');

  // Redeban: "YYYY/MM/DD"
  if (medioPago === 'Redeban') {
    return parseYYYYMMDD(s) || parseFlexible(s);
  }

  // Bre-B: "DD/MM/YY"
  if (medioPago === 'Bre-B') {
    return parseDDMMYY(s) || parseFlexible(s);
  }

  // Otros (p. ej., Binance) → intenta flexible
  return parseFlexible(s);
}


// --- Normaliza años de 2 dígitos ---
function normalizarAnioYY(y) {
  // si siempre quieres 2000+, usa: return (y >= 0 && y <= 99) ? 2000 + y : y;
  if (y >= 0 && y <= 99) return y <= 69 ? (2000 + y) : (1900 + y);
  return y;
}

// Parsers específicos por medio
function parseYYYYMMDD(s) { // Redeban -> "2025/09/07" (también acepta "-")
  const t = String(s).trim().replace(/-/g, '/');
  const m = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(t);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const M = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  if (M < 1 || M > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, M - 1, d));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function parseDDMMYY(s) { // Bre-B -> "07/09/25" (también acepta "-")
  const t = String(s).trim().replace(/-/g, '/');
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/.exec(t);
  if (!m) return null;
  const d = parseInt(m[1], 10);
  const M = parseInt(m[2], 10);
  let y = parseInt(m[3], 10);
  y = normalizarAnioYY(y);
  if (M < 1 || M > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, M - 1, d));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

// Fallback flexible (por si otro proveedor cambia el formato)
function parseFlexible(s) {
  const t = String(s || '').trim().replace(/-/g, '/');
  const parts = t.split('/');
  if (parts.length !== 3) return null;
  let [a,b,c] = parts.map(v => parseInt(v,10));
  if ([a,b,c].some(n => Number.isNaN(n))) return null;
  let y, M, d;
  if (a > 31) { y=a; M=b; d=c; } else { d=a; M=b; y=c; }
  if (c <= 99) y = normalizarAnioYY(y);
  if (M < 1 || M > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, M - 1, d));
  return Number.isNaN(dt.getTime()) ? null : dt;
}



// Calcula DD/MM/YYYY en UTC
function calcularFechaCorte(fechaInicio, diasDuracion, medioPago) {
  try {
    const start = parsearFechaPorMedio(fechaInicio, medioPago);
    if (!start) return fechaInicio; // fallback si no se pudo parsear
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + (diasDuracion || 30));
    const dd = String(end.getUTCDate()).padStart(2, '0');
    const mm = String(end.getUTCMonth() + 1).padStart(2, '0');
    const yyyy = end.getUTCFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return fechaInicio;
  }
}


// Función para guardar en ventasOnline - UN DOCUMENTO POR PERFIL con ID automático
async function guardarVentasOnline(pagoData, credencialesEntregadas, medioPago, datosUsuario) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const ventasGuardadas = [];
    
    // Procesar cada credencial entregada
    for (const credencial of credencialesEntregadas) {
      try {
        // Obtener duración del producto desde el catálogo correcto según el rol del usuario
        const duracionTexto = await obtenerDuracionProducto(credencial.plataforma, user.uid);
        const diasDuracion = extraerDiasDeDuracion(duracionTexto);
        
        // Calcular fecha de corte
        const fechaCorte = calcularFechaCorte(pagoData.fecha, diasDuracion, medioPago);

        
        if (credencial.perfiles && credencial.perfiles.length > 0) {
          // Crear un documento por cada perfil entregado
          for (let i = 0; i < credencial.perfiles.length; i++) {
            const perfil = credencial.perfiles[i];
            
            const ventaData = {
              codigo: credencial.codigo,
              fechaInicio: pagoData.fecha,
              fechaCorte: fechaCorte,
              plataforma: credencial.plataforma,
              uid: user.uid,
              correo: datosUsuario.email,
              numero: datosUsuario.phoneNumber,
              idTransaccion: pagoData.mensajeId || pagoData.idmensaje,
              
              // Campos adicionales útiles
              medioPago: medioPago,
              hora: pagoData.hora,
              nombreComprador: pagoData.nombreCompleto || pagoData.usuario,
              diasDuracion: diasDuracion,
              duracionOriginal: duracionTexto,
              tipoCuenta: credencial.tipo,
              
              // Información específica del perfil
              nombrePerfil: perfil.nombre,
              pinPerfil: perfil.pin,
              numeroPerfil: i + 1,
              
              // Metadatos
              createdAt: serverTimestamp(),
              activo: true,
              estado: 'entregado'
            };

            // Usar addDoc para generar ID automático
            const ventaRef = await addDoc(collection(db, 'ventasOnline'), ventaData);
            
            ventasGuardadas.push({ 
              id: ventaRef.id, 
              codigo: credencial.codigo, 
              plataforma: credencial.plataforma,
              perfil: perfil.nombre 
            });
            console.log(`Venta guardada: ${ventaRef.id} - ${credencial.plataforma} (${credencial.codigo}) - Perfil: ${perfil.nombre}`);
          }
        } else {
          // Si no hay perfiles específicos (cuenta completa sin perfiles detallados)
          const ventaData = {
            codigo: credencial.codigo,
            fechaInicio: pagoData.fecha,
            fechaCorte: fechaCorte,
            plataforma: credencial.plataforma,
            uid: user.uid,
            correo: datosUsuario.email,
            numero: datosUsuario.phoneNumber,
            idTransaccion: pagoData.mensajeId || pagoData.idmensaje,
            
            medioPago: medioPago,
            hora: pagoData.hora,
            nombreComprador: pagoData.nombreCompleto || pagoData.usuario,
            diasDuracion: diasDuracion,
            duracionOriginal: duracionTexto,
            tipoCuenta: credencial.tipo,
            
            // Para cuenta completa sin perfiles específicos
            nombrePerfil: 'Cuenta Completa',
            pinPerfil: null,
            numeroPerfil: 1,
            
            createdAt: serverTimestamp(),
            activo: true,
            estado: 'entregado'
          };

          // Usar addDoc para generar ID automático
          const ventaRef = await addDoc(collection(db, 'ventasOnline'), ventaData);
          
          ventasGuardadas.push({ 
            id: ventaRef.id, 
            codigo: credencial.codigo, 
            plataforma: credencial.plataforma,
            perfil: 'Cuenta Completa' 
          });
          console.log(`Venta guardada: ${ventaRef.id} - ${credencial.plataforma} (${credencial.codigo}) - Cuenta Completa`);
        }
        
      } catch (error) {
        console.error(`Error guardando venta para ${credencial.codigo}:`, error);
      }
    }

    return { 
      success: true, 
      ventasGuardadas,
      total: ventasGuardadas.length 
    };

  } catch (error) {
    console.error('Error guardando ventas online:', error);
    return { success: false, error: error.message };
  }
}

const MediosPago = () => {
  const location = useLocation();
  const totalCompra = Number((location.state?.total || 0).toFixed(2));
  const productosCarrito = location.state?.cart || null; // Productos del carrito

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buyerName, setBuyerName] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState(null);
  const [credencialesEntregadas, setCredencialesEntregadas] = useState(null);
  const [showCreds, setShowCreds] = useState(false);
  const [mostrarCredencialesFuera, setMostrarCredencialesFuera] = useState(false);
  const [modalType, setModalType] = useState(''); // cambiar de isBrebModal a modalType
  const navigate = useNavigate(); 

  // Funciones para abrir los modales específicos
  const openBrebModal = () => {
    setModalType('breb');
    setIsModalOpen(true);
  };

  const openRedebanModal = () => {
    setModalType('redeban');
    setIsModalOpen(true);
  };

  const openBinanceModal = () => {
    setModalType('binance');
    setIsModalOpen(true);
  };

    const openTermsConditions = () => {
    window.open(TERMS_CONDITIONS_URL, '_blank', 'noopener,noreferrer');
  };

  const closeModal = useCallback(() => {
    // Si hay credenciales entregadas, mostrarlas fuera del modal
    if (credencialesEntregadas && credencialesEntregadas.length > 0) {
      setMostrarCredencialesFuera(true);
      setShowCreds(true); // Mostrar credenciales inmediatamente
    }
    
    setIsModalOpen(false);
    setVerifyMsg(null);
    setVerifying(false);
    setBuyerName('');
    // NO resetear credencialesEntregadas ni showCreds aquí
  }, [credencialesEntregadas]);

  const limpiarTodo = useCallback(() => {
    setIsModalOpen(false);
    setVerifyMsg(null);
    setVerifying(false);
    setBuyerName('');
    setCredencialesEntregadas(null);
    setShowCreds(false);
    setMostrarCredencialesFuera(false);
    
    navigate('/page-store');
  }, [navigate]);

  // Cerrar modal con ESC
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && closeModal();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeModal]);

  const buildCredencialesText = (credencialesList = []) => {
    const bloques = credencialesList.map((c, idx) => {
      const header = `#${idx + 1} ${c.plataforma} - ${c.tipo}`;
      const base = [
        `Código de Cuenta: ${c.codigo}`,
        `Email/Usuario: ${c.correo}`,
        `Contraseña: ${c.contrasena}`,
      ].filter(Boolean);

      const perfiles = (c.perfiles || []).map((p, i) => `- Perfil ${i + 1}: ${p.nombre} | PIN: ${p.pin}`);
      return [header, ...base, ...(perfiles.length ? ['Perfiles:', ...perfiles] : [])].join('\n');
    });
    return bloques.join('\n\n');
  };

  const copyAllCredenciales = async () => {
    try {
      if (!credencialesEntregadas || credencialesEntregadas.length === 0) return;
      const text = buildCredencialesText(credencialesEntregadas);
      await navigator.clipboard.writeText(text);
      setVerifyMsg(v => v ? ({ ...v, text: (v.text + '\n\n📋 Copiado al portapapeles.') }) : v);
    } catch (e) {
      console.error('Error copiando credenciales:', e);
      setVerifyMsg(v => v ? ({ ...v, text: (v.text + '\n\n⚠️ No se pudo copiar automáticamente. Selecciona y copia manualmente.') }) : v);
    }
  };

// Función modificada verificarPago para manejar correctamente los 3 tipos de pago
const verificarPago = async () => {
  setVerifyMsg(null);
  setCredencialesEntregadas(null);

  if (!buyerName || buyerName.trim().length < 3) {
    setVerifyMsg({ ok: false, text: 'Ingresa el nombre del titular de la cuenta bancaria.' });
    return;
  }
  if (!totalCompra || totalCompra <= 0) {
    setVerifyMsg({ ok: false, text: 'El total a verificar es inválido (0).' });
    return;
  }

  try {
    setVerifying(true);

    // IMPORTANTE: Construir URL correctamente según el patrón que funciona en breb.js
    let endpointUrl, medioPago, bodyData;
    
    // Limpiar la URL base por si tiene barras al final
    const baseUrl = CLOUD_FN_BASE_URL.replace(/\/$/, '');
    
    switch(modalType) {
      case 'binance':
        endpointUrl = `${baseUrl}/binance`;
        medioPago = 'Binance';
        bodyData = {
          usuario: buyerName.trim(),
          monto: totalCompra
        };
        break;
      case 'breb':
        endpointUrl = `${baseUrl}/breb`;
        medioPago = 'Bre-B';
        bodyData = {
          nombre: buyerName.trim(),
          monto: totalCompra
        };
        break;
      default: 
        endpointUrl = `${baseUrl}/bancolombia`; 
        medioPago = 'Redeban';
        bodyData = {
          nombre: buyerName.trim(),
          monto: totalCompra
        };
        break;
    }
    
    console.log('🌍 CLOUD_FN_BASE_URL:', CLOUD_FN_BASE_URL);
    console.log('🌍 Base URL limpia:', baseUrl);
    console.log('🌍 Endpoint final:', endpointUrl);
    console.log('📝 Modal type:', modalType);
    console.log('📝 Datos a enviar:', bodyData);
    
    // 1. Verificar pago en Gmail
    const res = await fetch(endpointUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyData)
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.error('❌ Error HTTP:', res.status, res.statusText, errorText);
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }

    const data = await res.json();
    console.log('✅ Respuesta del servidor:', data);

    if (data?.success) {
      const pagoData = data.data;

      // 2. Obtener datos del usuario
      const datosUsuario = await obtenerDatosUsuario(auth.currentUser.uid);

      // 3. Guardar en Paycheck
      try {
        const resSave = await guardarPaycheckUnico(pagoData);
        
        if (resSave.already) {
          // El pago ya existía, verificar si ya fue procesado
          const paycheckRef = doc(db, 'Paycheck', pagoData.mensajeId);
          const paycheckDoc = await getDoc(paycheckRef);
          const paycheckExistente = paycheckDoc.data();
          
          if (paycheckExistente.flag === true) {
            setVerifyMsg({
              ok: false,
              text: `Esta compra ya fue entregada anteriormente con el código ${pagoData.mensajeId}. Las credenciales ya fueron proporcionadas.`
            });
            return;
          }
        }

        // 4. Entregar credenciales del carrito
        const resultadoEntrega = await entregarCredencialesCarrito(
          pagoData.mensajeId, 
          totalCompra, 
          pagoData.nombreCompleto || pagoData.usuario, // Binance usa 'usuario', no 'nombreCompleto'
          productosCarrito
        );

        if (resultadoEntrega.success) {
          // 5. Guardar recibo en la colección receipts
          const resultadoRecibo = await guardarRecibo(
            pagoData, 
            resultadoEntrega.credenciales, 
            productosCarrito, 
            medioPago, 
            totalCompra
          );

          // 6. Guardar en ventasOnline
          const resultadoVentas = await guardarVentasOnline(
            pagoData,
            resultadoEntrega.credenciales,
            medioPago,
            datosUsuario
          );

          if (!resultadoRecibo.success) {
            console.error('Error guardando recibo:', resultadoRecibo.error);
          }

          if (!resultadoVentas.success) {
            console.error('Error guardando ventas online:', resultadoVentas.error);
          } else {
            console.log(`✅ ${resultadoVentas.total} ventas guardadas en ventasOnline`);
          }

          setVerifyMsg({
            ok: true,
            text: `✅ Pago ${medioPago} verificado y credenciales entregadas\nFecha: ${pagoData.fecha} • Hora: ${pagoData.hora} • ID: ${pagoData.mensajeId}`
          });
          setCredencialesEntregadas(resultadoEntrega.credenciales);
        } else {
          setVerifyMsg({
            ok: false,
            text: resultadoEntrega.message
          });
        }

      } catch (err) {
        console.error('❌ Error en el proceso:', err);
        setVerifyMsg({
          ok: false,
          text: '❌ Error procesando la compra. Contacta soporte.'
        });
      }
    } else {
      setVerifyMsg({
        ok: false,
        text: data?.message || `❌ No se encontró pago ${medioPago} coincidente.`,
      });
    }
  } catch (err) {
    console.error('❌ Error completo al conectar:', err);
    setVerifyMsg({ ok: false, text: `❌ Error al conectar con el servidor: ${err.message}` });
  } finally {
    setVerifying(false);
  }
};

  return (
    <>
      <NavBar />
      <div className={styles.wrapper}>
        <h1 className={styles.title}>Medios de Pago</h1>
        
        <div className={styles.buttonsContainer}>
          {/* Redeban: abre modal con la IMAGEN del QR */}
          <div className={styles.payCard}>
            <button
              className={styles.payButton}
              onClick={openRedebanModal}
              title="Pagar con Redeban (mostrar QR)"
            >
              <img src={redebanLogo} alt="Redeban" className={styles.payIcon} />
            </button>
            <p className={styles.payText}>Redeban (Colombia)</p>
          </div>

          {/* Breb: abre modal con la llave */}
          <div className={styles.payCard}>
            <button
              className={styles.payButton}
              onClick={openBrebModal}
              title="Pagar con llave"
            >
              <img src={breb} alt="Bre-B" className={styles.payIcon} />
            </button>
            <p className={styles.payText}>Paga con llave (Colombia)</p>
          </div>

          {/* Binance: abre modal con QR */}
          <div className={styles.payCard}>
            <button
              className={styles.payButton}
              onClick={openBinanceModal}
              title="Pagar con Binance"
            >
              <img src={binanceLogo} alt="Binance" className={styles.payIcon} />
            </button>
            <p className={styles.payText}>Binance Pay (USDT)</p>
          </div>
        </div>

        {/* Modal Unificado - Muestra contenido según modalType */}
        {isModalOpen && (
          <>
            <div className={styles.modalOverlay} onClick={closeModal} />
            <div
              className={styles.modal}
              role="dialog"
              aria-modal="true"
              aria-label="Modal de pago"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header con botón de cerrar */}
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>
                  {modalType === 'breb' ? 'Llave de pago Bre-B' : 
                   modalType === 'binance' ? 'Escanea tu QR de Binance' : 
                   'Escanea tu QR de Redeban'}
                </h2>
                <button
                  className={styles.modalClose}
                  onClick={closeModal}
                  aria-label="Cerrar"
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>

              <p className={styles.modalSubtitle}>
                Valor: <strong>${totalCompra.toLocaleString('es-CO')}</strong>
              </p>

              {/* Mostrar el contenido según el tipo de pago */}
              {modalType === 'binance' ? (
                // Contenido para Binance (QR)
                <>
                  {/* Instrucciones para Binance */}
                  <div className={styles.instructions}>
                    <ol>
                        <div className={styles.example}>
                          1. Abre tu app de Binance y escanea el QR de pago.
                          <br />2. Envía el valor exacto: <strong>${totalCompra.toLocaleString('es-CO')} USDT</strong>
                          <br />3. Completa el pago.
                          <br />4. <strong>IMPORTANTE: </strong> Ingresa tu usuario de Binance tal como estas registrad@ en la app.
                          <br />5. Presiona <strong>Verificar pago</strong> para recibir tu producto al instante.
                        </div>
                    </ol>
                  </div>

                  {/* Caja blanca con QR de Binance */}
                  <div className={styles.qrWhiteBox} style={{ display: 'grid', placeItems: 'center' }}>
                    <img
                      src={binanceQR}
                      alt="Código QR Binance para pago"
                      className={styles.qrImage}
                    />
                  </div>
                </>
              ) : modalType === 'breb' ? (
                // Contenido para Bre-B (Llave)
                <>
                  <div className={styles.keyBox}>
                    <p><strong>Llave:</strong> 3188875678</p>
                    <button
                      className={styles.copyBtn}
                      onClick={() => navigator.clipboard.writeText('3188875678')}
                      title="Copiar llave"
                    >
                      📋 Copiar llave
                    </button>
                  </div>

                  {/* Instrucciones para Bre-B */}
                  <div className={styles.instructions}>
                    <ol>
                        <div className={styles.example}>
                          1. Abre tu app bancaria.
                          <br />2. Selecciona Tranferir con llaves (Bre-B).
                          <br />3. Ingresa la llave: <strong>3188875678</strong>
                          <br />4. Transfiere el valor exacto: <strong>${totalCompra.toLocaleString('es-CO')}</strong>
                          <br />5. Completada la transferencia, ingresa el nombre del titular de la cuanta y presiona <strong>Verificar pago</strong>.
                          <br />Si el pago es correcto, recibirás tu producto al instante.
                        </div>
                    </ol>
                  </div>
                </>
              ) : (
                // Contenido para Redeban (QR)
                <>
                  {/* Instrucciones para Redeban */}
                  <div className={styles.instructions}>
                    
                  <ol>
                        <div className={styles.example}>
                          1. Abre tu app bancaria y escanea el QR de pago.
                          <br />2. Transfiere el valor exacto: <strong>${totalCompra.toLocaleString('es-CO')}</strong>
                          <br />3. Ingresa el nombre del titular de la cuenta.
                          <br />4. Completado el pago, presiona <strong>Verificar pago</strong> abajo.
                          <br />Si el pago es correcto, recibirás tu producto al instante.
                        </div>
                  </ol>   
                    
                  </div>

                  {/* Caja blanca con tu imagen de QR */}
                  <div className={styles.qrWhiteBox} style={{ display: 'grid', placeItems: 'center' }}>
                    <img
                      src={redebanQR}
                      alt="Código QR Redeban para pago"
                      className={styles.qrImage}
                    />
                  </div>
                </>
              )}

              {/* Formulario para verificación */}
              <div className={styles.formGroup} style={{ marginTop: '1rem' }}>
                <label className={styles.formLabel}>
                  {modalType === 'binance' 
                    ? 'Escribe tu nombre de usuario de Binance:'
                    : 'Escribe el nombre del titular de la cuenta bancaria:'
                  }
                </label>
                <input
                  className={styles.input}
                  type="text"
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  placeholder={modalType === 'binance' ? "Tu usuario de Binance" : "Ej: JUAN JOSE PEREZ GOMEZ"}
                />
              </div>

              <div className={styles.actionsRow} style={{ justifyContent: 'center', marginTop: '.75rem' }}>
                {/* Botón de descarga para Redeban */}
                {modalType === 'redeban' && (
                  
                  <a
                    href={redebanQR}
                    download="QR-Redeban.png"
                    className={styles.secondaryBtn}
                    title="Descargar imagen del QR"
                  >
                    ⬇️ Descargar QR
                  </a>

                )}

                {/* Botón de descarga para Binance */}
                {modalType === 'binance' && (
                  <a
                    href={binanceQR}
                    download="QR-Binance.png"
                    className={styles.secondaryBtn}
                    title="Descargar imagen del QR"
                  >
                    ⬇️ Descargar QR
                  </a>
                )}

                <button
                  className={styles.primaryBtn}
                  onClick={verificarPago}
                  disabled={verifying || !buyerName || !totalCompra}
                  title={!buyerName ? 'Ingresa el nombre' : undefined}
                >
                  {verifying ? '⏳ Verificando…' : '🔍 Verificar pago'}
                </button>

                <button className={styles.secondaryBtn} onClick={closeModal}>
                  Cerrar
                </button>
              </div>

              

              {/* Resultado verificación */}
              {verifyMsg && (
                <div
                  className={`${styles.resultBox} ${verifyMsg.ok ? styles.ok : styles.fail}`}
                  role="status"
                >
                  {verifyMsg.text}
                  {/* Botón para cerrar modal después de verificación exitosa */}
                  {verifyMsg.ok && (
                    <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                      <button
                        className={styles.primaryBtn}
                        onClick={closeModal}
                        style={{ fontSize: '0.9rem' }}
                      >
                        Cerrar y ver credenciales
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Tips si falla - DENTRO del modal */}
              {verifyMsg && !verifyMsg.ok && (
                <div className={styles.hintBox}>
                  <strong>¿Salió como "no realizado"?</strong>
                  <ul>
                    <li>Revisa que la transferencia sea el <strong>valor exacto</strong> de la compra.</li>
                    <li>Verifica que el <strong>nombre</strong> coincida con el <strong>titular de la cuenta bancaria</strong>{modalType === 'binance' ? ' o usuario de Binance' : ''}.</li>
                    <li>Si acabas de pagar, espera 1 minuto y vuelve a intentar.</li>
                    <li>Si el problema persiste contáctanos al <strong>WhatsApp</strong>: +57 320 7499845 o +57 316 0789935</li>
                  </ul>
                </div>
              )}

                                <div className={styles.instructions}>
                    
                  <ol>
                        <div className={styles.example}>
                          <strong>POLÍTICAS DE USO</strong>
                          <br />Cada perfil esta autorizado para utilizarse únicamente en un dispositivo a la vez.
                          <br /><strong>IMPORTANTE: Esta prohibido la modificación de nombre de perfil, PIN o cualquier otra configuracion de la cuenta entregada</strong> 
                          <br />Detectar el uso simultáneo de multiples dispositivos con un mismo perfil constituye una violación directa de nuestras normas.
                          <br />En caso de reicidencia, se procederá a la suspensión inmediata del acceso a la cuenta sin derecho a reembolso ni garantía, ya que se considera un incumplimiento de los términos acordados.
                          <br />En plataformas como Netflix, Chat GPT y Prime Vido limitamos la vinculación a un solo dispositivo debido a políticas de dichas empresas.
                          <br />Quieres conocer más, te invitamos a leer nuestros  <button
                                            type="button"
                                            className="policy-link"
                                            onClick={openTermsConditions}
                                          >
                                            términos y condiciones
                                            <ExternalLink size={12} />
                                          </button>
                        </div>
                        <br />

                  </ol>   
                    
                  </div>
            </div>
          </>
        )}

        {/* Mostrar credenciales si fueron entregadas - FUERA del modal */}
        {credencialesEntregadas && credencialesEntregadas.length > 0 && mostrarCredencialesFuera && (
          <div className={styles.credencialesBox}>
            {/* Portada antes de mostrar credenciales */}
            {!showCreds ? (
              <div className={styles.lockCard}>
                <img
                  src={CREDENTIALS_COVER_IMG}
                  alt="Portada credenciales"
                  className={styles.coverImage}
                />
                <p className={styles.lockText}>
                  Tus credenciales están listas. Pulsa para mostrarlas.
                </p>
                <button
                  className={styles.revealBtn}
                  onClick={() => setShowCreds(true)}
                  title="Mostrar credenciales"
                >
                  🔓 Mostrar credenciales
                </button>
              </div>
            ) : (
              <>
                <div className={styles.credencialesHeader}>
                  <h3>🎉 ¡Tus credenciales!</h3>
                  <div className={styles.copyRow}>
                    <button
                      className={styles.copyBtn}
                      onClick={copyAllCredenciales}
                      title="Copiar toda la información"
                    >
                      📋 Copiar todo
                    </button>
                  </div>
                </div>

                {credencialesEntregadas.map((credencial, index) => (
                  <div key={index} className={styles.credencialesItem}>
                    <h4 className={styles.credencialHeader}>
                      {credencial.plataforma} - {credencial.tipo}
                    </h4>
                    
                    <div className={styles.credencialesInfo}>
                      <div className={styles.credencialItem}>
                        <strong>Código de Cuenta:</strong> {credencial.codigo}
                      </div>
                      <div className={styles.credencialItem}>
                        <strong>Email/Usuario:</strong> {credencial.correo}
                      </div>
                      <div className={styles.credencialItem}>
                        <strong>Contraseña:</strong> {credencial.contrasena}
                      </div>
                      

                    </div>

                    {credencial.perfiles && credencial.perfiles.length > 0 && (
                      <div className={styles.perfilesEntregados}>
                        <h5>Perfiles entregados:</h5>
                        {credencial.perfiles.map((perfil, perfilIndex) => (
                          <div key={perfilIndex} className={styles.perfilItem}>
                            <span><strong>Perfil:</strong> {perfil.nombre}</span>
                            <span><strong>PIN:</strong> {perfil.pin}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className={styles.credencialesFooter}>
                  <p>📱 Guarda esta información en un lugar seguro</p>
                  <p>Puedes consultar tus credenciales en <strong>Mi Cuenta → Cuentas Activas → Ir a Cuentas activas</strong></p>
                </div>

                <div className={styles.nuevaCompraBtn} style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button
                  className={styles.primaryBtn}
                  onClick={limpiarTodo}
                >
                  🛒 Nueva compra
                </button>
              </div>
              </>
            )}
          </div>
        )}

        {/* Tips si falla - FUERA del modal cuando no hay modal abierto */}
        {verifyMsg && !verifyMsg.ok && !isModalOpen && (
          <div className={styles.hintBox}>
            <strong>¿Salió como "no realizado"?</strong>
            <ul>
              <li>Revisa que la transferencia sea el <strong>valor exacto</strong> de la compra.</li>
              <li>Verifica que el <strong>nombre</strong> coincida con el <strong>titular de la cuenta bancaria</strong>.</li>
              <li>Si acabas de pagar, espera 1 minuto y vuelve a intentar.</li>
              <li>Si el problema persiste contáctanos al <strong>WhatsApp</strong>: +57 320 7499845 o +57 316 0789935</li>
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default MediosPago;